import type { Feature } from '../../app/feature';
import type { Prize } from '../../shared/types';
import { esc, q, qa } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { bus } from '../../shared/events';
import { fmt, money } from '../../shared/format';
import { commit, getState, uid } from '../../shared/store/store';
import { eventPrizes, leadById, pendingPrizes, pozoOf, prizeById } from '../../shared/store/selectors';
import { currentEvent, currentUser } from '../../shared/session/session';
import { toast } from '../../shared/ui/toast';
import { ApiError } from '../../shared/api/http';
import { eventsApi } from '../events/events.api';
import { hydrateFromApi } from '../../shared/store/sync';
import { dramaticOrder, eligiblePool, pickWinner, randomSeed, revealOrder, sha256, survivors } from '../../domain/draw-engine';
import { live } from './live';
import { Snd } from './audio';
import { consoleTemplate, toggleConsole, updateConsole } from './console';
import { sizeCanvases } from './fx/canvas';
import { startMotes } from './fx/motes';
import { confettiBurst } from './fx/confetti';
import { buildLaurel } from './fx/laurel';
import { clearJackpot, coinBlast, jackpot, rollMoney } from './fx/coins';
import { drawWheel, spinWheel, startWheelLoop } from './wheel';
import { serverNow } from '../../shared/clock';

/**
 * Estúdio ao vivo — o programa em quatro atos:
 *   0. abertura  1. a roda escolhe o prêmio  2. revelação dígito a dígito  3. ganhador
 *
 * >>> O sorteio (semente, selo, ganhador, ordem) roda aqui no navegador. Em produção,
 * `startDraw` chama a API em dois passos (selo antes, resultado depois) e o resto
 * deste módulo continua só animando.
 */
export const studio: Feature = {
  template: () => `
<div id="stage" hidden>
  <div class="stage-bg" aria-hidden="true">
    <div class="sl sl-beams"></div><div class="sl sl-key"></div><div class="sl sl-floor"></div>
    <div class="sl sl-vig"></div><div class="sl sl-grain"></div>
  </div>
  <canvas id="motes" aria-hidden="true"></canvas>
  <canvas id="coins" aria-hidden="true"></canvas>
  <canvas id="confetti" aria-hidden="true"></canvas>
  <div id="flash" aria-hidden="true"></div>

  <div class="stage-in">
    <div class="stage-top">
      <span class="onair"><i></i><span data-i18n="stage.onair">En vivo</span></span>
      <div class="stage-mark" aria-hidden="true"></div>
      <div class="wordmark">Rueda de la Fortuna<small>Apueste Rápido</small></div>
      <div class="spacer"></div>
      <div class="pozo">
        <div class="z-coin">🪙</div>
        <div class="z-t"><div class="z-label" data-i18n="stage.pozo">Pozo de la noche</div><div class="z-val tnum" id="stage-pozo">$0</div></div>
      </div>
      <div class="seal" id="stage-seal"></div>
      <div class="meters">
        <div class="meter is-prize"><div class="m-label" data-i18n="stage.metaPrizes">Premios en juego</div><div class="m-val tnum" id="m-prizes">0</div></div>
        <div class="meter is-pool"><div class="m-label" data-i18n="stage.inplay">Cartelas en juego</div><div class="m-val tnum" id="stage-pool">0</div></div>
      </div>
    </div>

    <div class="stage-body">
      <!-- ATO 0 — abertura -->
      <div class="act on" id="act-idle">
        <div class="opener">
          <div class="o-sub" id="o-sub">Sorteo de la comunidad</div>
          <div class="o-title">Rueda de la Fortuna</div>
          <div class="o-rule" aria-hidden="true"></div>
          <div class="o-motto" data-i18n="stage.motto">La rueda gira para todos · La Fortuna es ciega</div>
          <div class="pool-line">
            <span data-i18n="stage.players">Jugadores</span> <b class="tnum" id="idle-players">0</b>
            <span data-i18n="stage.cartelas">Cartelas</span> <b class="tnum" id="idle-cartelas">0</b>
          </div>
          <div class="lineup" id="lineup"></div>
          <div class="prog" id="idle-prog" aria-hidden="true"></div>
        </div>
      </div>

      <!-- ATO 1 — a roda escolhe o prêmio -->
      <div class="act" id="act-wheel">
        <div class="wheel-wrap"><canvas id="wheel" aria-label="Rueda de premios"></canvas></div>
      </div>

      <!-- ATO 2 — tabuleiro de dígitos -->
      <div class="act" id="act-draw">
        <div class="prize-plate">
          <div class="p-emoji" id="draw-emoji">🎁</div>
          <div class="p-t"><div class="p-kicker" data-i18n="stage.playingfor">Se juega por</div><div class="p-name" id="draw-prize">—</div></div>
        </div>
        <div class="slot-frame"><div class="board" id="board"></div></div>
        <div class="reveal-dots" id="reveal-dots" aria-hidden="true"></div>
        <div class="alive">
          <div class="pool-line">
            <span data-i18n="stage.remaining">Cartelas vivas</span>
            <b class="tnum" id="draw-pool">0</b>
            <span class="down" id="draw-elim"></span>
          </div>
          <div class="alive-bar"><i id="alive-fill"></i></div>
          <div class="seal-pill" id="draw-seal"></div>
        </div>
        <div class="duel-flag" id="duel-flag" hidden data-i18n="stage.duel">⚔ Duelo final — un dígito decide</div>
        <div class="finalists" id="draw-finalists"></div>
      </div>

      <!-- ATO 3 — ganhador -->
      <div class="act" id="act-winner">
        <div class="winner">
          <div class="burst" aria-hidden="true"></div>
          <div class="laurel" id="laurel" aria-hidden="true"></div>
          <div class="w-kicker" data-i18n="stage.winner">¡Ganador!</div>
          <div class="w-name" id="win-name">—</div>
          <div class="w-tg" id="win-tg">—</div>
          <div class="w-num" id="win-num"></div>
          <div class="money-row"><div class="money tnum" id="win-money">$0</div><div class="money-cur">CLP</div></div>
          <div class="prize-plate">
            <div class="p-emoji" id="win-emoji">🎁</div>
            <div class="p-t"><div class="p-kicker" data-i18n="stage.takes">Se lleva</div><div class="p-name" id="win-prize">—</div></div>
          </div>
          <div class="spoken" id="win-said">✦  La Fortuna ha hablado  ✦</div>
        </div>
      </div>
    </div>

    <div class="stage-foot">
      <div class="cta-strip"><b>🎯</b><span id="cta-text"></span></div>
      <div class="sf-label" data-i18n="stage.wonToday">Últimos ganadores</div>
      <div class="won-strip" id="won-strip"></div>
    </div>
  </div>

  ${consoleTemplate()}
</div>`,

  mount() {
    q('#c-exit').onclick  = closeStage;
    q('#c-next').onclick  = liveNext;
    q('#c-wheel').onclick = spinWheel;
    q('#c-skip').onclick  = skipPrize;
    q('#c-mute').onclick  = () => { q('#c-mute').textContent = Snd.toggle() ? '🔇' : '🔊'; };
    q('#c-hide').onclick  = () => toggleConsole(true);
    q('#console-tab').onclick = () => toggleConsole(false);
    q('#c-full').onclick  = () => {
      if (document.fullscreenElement) void document.exitFullscreen();
      else void document.documentElement.requestFullscreen?.().catch(() => { /* negado */ });
    };
    bus.on('lang', () => { if (isOpen()) { ctaIdx = 0; startCTA(); } });
    buildLaurel();
    drawWheel();
    if (document.fonts?.ready) void document.fonts.ready.then(() => drawWheel(0));
  },

  render() { /* o palco se desenha ao abrir e a cada passo do sorteio */ }
};

export const isOpen = (): boolean => !q('#stage').hidden;

export function openStage(): void {
  // regra de negócio: só o influenciador (anfitrião) conduz o sorteio; o painel administra, não sorteia
  if (currentUser()?.role !== 'HOST') { toast(t('msg.hostOnly')); return; }
  const ev = currentEvent();
  if (!ev || !eventPrizes(ev.id).length) { toast(t('msg.noevent')); return; }
  if (ev.status === 'done' || ev.status === 'cancelled') { toast(t('msg.eventDone')); return; }
  // o estúdio só abre no horário do sorteio (a API também recusa resultado antes dele)
  const at = currentUser()?.event?.scheduledAt;
  if (!at) { toast(t('msg.studioNoDate')); return; }
  if (serverNow() < Date.parse(at)) { toast(t('msg.studioLocked')); return; }
  q('#stage').hidden = false;
  document.body.style.overflow = 'hidden';
  Snd.resume();
  resetLive();
  sizeCanvases();
  startMotes();
  startCTA();
}

export function closeStage(): void {
  q('#stage').hidden = true;
  q('#stage').classList.remove('suspense', 'win');
  document.body.style.overflow = '';
  window.clearInterval(ctaTimer);
}

/** Troca o ato visível. */
export function act(name: 'idle' | 'wheel' | 'draw' | 'winner'): void {
  qa('.act').forEach(a => a.classList.toggle('on', a.id === 'act-' + name));
  if (name === 'wheel') startWheelLoop();
}

/* mensagem rotativa: converte quem assiste em quem participa */
let ctaIdx = 0, ctaTimer: number | undefined;
function startCTA(): void {
  const set = (): void => { q('#cta-text').textContent = t('cta.' + (ctaIdx % 4 + 1)); ctaIdx++; };
  set(); window.clearInterval(ctaTimer); ctaTimer = window.setInterval(set, 9000);
}

export function resetLive(): void {
  window.clearTimeout(live.winTimer);
  live.phase = 'idle'; live.prize = null; live.winner = null; live.step = 0;
  live.busy = false; live.revealing = false;
  const ev = currentEvent();
  live.event = ev;
  const pool = eligiblePool(getState(), ev);
  const pend = ev ? pendingPrizes(ev.id) : [];

  q('#o-sub').textContent         = ev ? ev.name : t('stage.season');
  q('#stage-pool').textContent    = fmt(pool.length);
  q('#stage-pozo').textContent    = money(ev ? pozoOf(ev.id) : 0);
  q('#m-prizes').textContent      = String(pend.length);
  q('#idle-players').textContent  = fmt(new Set(pool.map(c => c.leadId)).size);
  q('#idle-cartelas').textContent = fmt(pool.length);
  renderLineup(ev, pend);
  q('#stage-seal').innerHTML = '';
  q('#stage').classList.remove('suspense');
  clearJackpot();
  act('idle');
  updateConsole();
  renderWonStrip();
}

function renderWonStrip(): void {
  const list = getState().draws.slice(-14).reverse();
  q('.stage-foot').classList.toggle('no-won', list.length === 0);
  q('#won-strip').innerHTML = list.map(d => {
    const l = leadById(d.leadId), p = prizeById(d.prizeId);
    return `<div class="w"><b>${esc(l ? l.name : '—')}</b><span>${d.number}</span>${p ? p.emoji : ''}</div>`;
  }).join('') || `<div class="w muted">—</div>`;
}

/** Arranca o sorteio de um prêmio: semente, selo na tela, ganhador já decidido, ordem de abertura. */
export async function startDraw(prize: Prize): Promise<void> {
  // o servidor é a verdade: se este prêmio já saiu ou o sorteio fechou em outra aba, não sorteia de novo
  if (live.busy) return;
  live.busy = true; updateConsole();
  try { await hydrateFromApi(); } catch { /* sem rede: segue com o que há localmente; o servidor recusa depois se for o caso */ }
  live.busy = false;
  const fresh = prizeById(prize.id);
  const ev = currentEvent();
  if (!ev || ev.status === 'done' || ev.status === 'cancelled') { toast(t('msg.eventDone')); resetLive(); return; }
  if (!fresh || fresh.status !== 'pending') { toast(t('msg.alreadyDrawn')); resetLive(); return; }
  prize = fresh;
  const s = getState();
  const pool = eligiblePool(s, ev);
  if (!pool.length) { toast(t('msg.nopool')); resetLive(); return; }
  live.prize = prize;
  live.pool  = pool;
  live.seed  = randomSeed();
  live.hash  = await sha256(live.seed);
  const { winner } = await pickWinner(pool, prize.id, live.seed);
  live.winner = winner;
  live.digits = winner.number.length;              // o tabuleiro se adapta ao número real
  if (s.config.dramaticFinal !== false) {
    const d = dramaticOrder(pool, winner.number.padStart(live.digits, '0'), live.digits);
    live.order = d.order; live.finalists = d.finalists;
  } else {
    live.order = revealOrder(live.digits, s.config.order); live.finalists = 1;
  }
  live.step  = 0;
  live.phase = 'draw';

  q('#draw-emoji').textContent = prize.emoji;
  q('#draw-prize').textContent = prize.name;
  q('#draw-pool').textContent  = fmt(pool.length);
  q('#stage-pool').textContent = fmt(pool.length);
  q('#m-prizes').textContent   = String(live.event ? pendingPrizes(live.event.id).length : 0);
  q('#stage-pozo').textContent = money(live.event ? pozoOf(live.event.id) : 0);
  q('#stage-seal').innerHTML   = `🔒 SHA-256 <b>${live.hash.slice(0, 16)}</b>`;
  q('#draw-finalists').innerHTML = '';
  q('#draw-finalists').classList.remove('duel');
  q('#duel-flag').hidden = true;
  q('#draw-elim').textContent = '';
  live.prevAlive = pool.length;
  buildBoard();
  renderRevealDots();
  setAliveBar(pool.length, pool.length);
  q('#draw-seal').innerHTML = `<i>🔒</i><span>${esc(t('stage.sealShort'))}</span><b>${live.hash.slice(0, 12)}…</b>`;
  act('draw');
  updateConsole();
}

/** Prêmios da noite como cartões: o próximo em destaque, os já sorteados com o número e o nome. */
function renderLineup(ev: ReturnType<typeof currentEvent>, pend: Prize[]): void {
  const all = ev ? eventPrizes(ev.id).filter(p => p.status !== 'cancelled') : [];
  const draws = getState().draws;
  const nextId = pend[0]?.id;
  let n = 0;
  q('#lineup').innerHTML = all.length ? all.map(p => {
    const d = draws.find(x => x.prizeId === p.id);
    const isNext = p.id === nextId;
    n++;
    const tag = d ? t('stage.drawnTag') : isNext ? t('stage.nextPrize') : `${t('stage.prizeN')} ${n}`;
    const foot = d
      ? `<div class="lp-win"><span>${esc(d.number)}</span>${esc(leadById(d.leadId)?.name.split(' ')[0] ?? '')}</div>`
      : `<div class="lp-val">${p.value > 0 ? money(p.value) : esc(t('ev.noValue'))}</div>`;
    return `<div class="lp-card${isNext ? ' next shine' : ''}${d ? ' drawn' : ''}">
      <div class="lp-tag">${esc(tag)}</div>
      <div class="lp-emoji">${esc(p.emoji || '🎁')}</div>
      <div class="lp-name">${esc(p.name)}</div>
      ${foot}
    </div>`;
  }).join('') : `<div class="lp-card"><div class="lp-name">${esc(t('live.done'))}</div></div>`;
  q('#idle-prog').innerHTML = all.map(p => `<i class="${p.status === 'drawn' ? 'done' : p.id === nextId ? 'next' : ''}"></i>`).join('');
}

/** Uma bolinha por dígito, na ordem de abertura: acesa quando já saiu, pulsando na que vem agora. */
function renderRevealDots(): void {
  q('#reveal-dots').innerHTML = live.order.map((_, i) =>
    `<i class="${i < live.step ? 'done' : i === live.step ? 'now' : ''}"></i>`).join('');
}

/** Barra de cartelas vivas: o bolão vai encolhendo até sobrar uma. */
function setAliveBar(alive: number, total: number): void {
  const pct = total > 0 ? Math.max(1.2, (alive / total) * 100) : 0;
  q('#alive-fill').style.width = `${pct}%`;
  q('#alive-fill').classList.toggle('few', alive <= 3);
}

/** Número com os dígitos já revelados em dourado e os demais apagados (na ordem real do número). */
function markedNumber(num: string, revealed: number[]): string {
  return num.padStart(live.digits, '0').split('').map((ch, i) =>
    `<b class="${revealed.includes(i) ? 'hit' : ''}">${ch}</b>`).join('');
}

function buildBoard(): void {
  const b = q('#board'); b.innerHTML = '';
  for (let i = 0; i < live.digits; i++) {
    const d = document.createElement('div');
    d.className = 'dig'; d.dataset.i = String(i);
    d.innerHTML = '<span>?</span>';
    b.appendChild(d);
  }
}

/** Revela o próximo dígito com suspense. */
function revealNext(): void {
  if (live.busy || !live.winner) return;
  const pos    = live.order[live.step] as number;
  const tile   = q(`.dig[data-i="${pos}"]`);
  const target = live.winner.number[pos] as string;
  const last   = live.step === live.digits - 1;
  live.busy = true;

  // o palco baixa as luzes e só fica aceso o dígito em jogo
  q('#stage').classList.add('suspense');
  qa('.dig').forEach(d => { if (d !== tile && !d.classList.contains('locked')) d.classList.add('dim'); });
  tile.classList.remove('dim'); tile.classList.add('rolling');
  updateConsole();                       // o botão apaga enquanto o dígito roda
  Snd.riser(last ? 2.1 : 1.35);

  const span = tile.firstElementChild as HTMLElement;
  let ticks = 0;
  const total = (last ? 34 : 24) + Math.floor(Math.random() * 8);
  const roll = window.setInterval(() => {
    span.textContent = String(Math.floor(Math.random() * 10));
    Snd.tick();
    if (++ticks >= total) {
      window.clearInterval(roll);
      span.textContent = target;
      tile.classList.remove('rolling'); tile.classList.add('locked');
      qa('.dig').forEach(d => d.classList.remove('dim'));
      q('#stage').classList.remove('suspense');
      Snd.lock();
      live.step++;
      afterReveal();
    }
  }, (last ? 62 : 52) + live.step * 12);
}

function afterReveal(): void {
  if (!live.winner) return;
  const done   = live.order.slice(0, live.step);
  const before = live.prevAlive;
  const alive  = survivors(live.pool, live.winner.number, done, live.digits);
  live.prevAlive = alive.length;

  animateCount(q('#draw-pool'), alive.length);
  const cut = before - alive.length;
  const elim = q('#draw-elim');
  elim.textContent = cut > 0 ? `−${fmt(cut)} ${t('stage.elim')}` : '';
  elim.classList.remove('pop'); void elim.offsetWidth; elim.classList.add('pop');
  renderRevealDots();
  setAliveBar(alive.length, live.pool.length);

  // último dígito por abrir e mais de um vivo: isto é um duelo
  const duel = live.step === live.digits - 1 && alive.length >= 2;
  const box  = q('#draw-finalists');
  q('#duel-flag').hidden = !duel;
  box.classList.toggle('duel', duel);

  // com poucos vivos mostramos NOMES: o lead reconhece a galera e fica
  box.innerHTML = (alive.length <= 14 && live.step < live.digits)
    ? alive.map((c, i) => {
        const l = leadById(c.leadId);
        return `<span class="f" style="animation-delay:${i * 45}ms"><b>${esc(l ? l.name.split(' ')[0] : '—')}</b><span class="f-num">${markedNumber(c.number, done)}</span></span>`;
      }).join('')
    : '';

  if (duel) Snd.heartbeat();

  live.busy = false;                     // primeiro libera, depois repinta o console:
  if (live.step >= live.digits) {        // ao contrário o botão fica desabilitado para sempre
    live.phase = 'winner';
    live.revealing = true;               // 420 ms de respiro antes de mostrar o ganhador:
    window.clearTimeout(live.winTimer);  // nesse intervalo o botão fica bloqueado, senão
    live.winTimer = window.setTimeout(showWinner, 420);   // um clique rápido confirma um ganhador que ainda não existe
  }
  updateConsole();
}

function animateCount(el: HTMLElement, to: number): void {
  const from = parseInt((el.textContent ?? '').replace(/\D/g, ''), 10) || 0;
  const t0 = performance.now(), dur = 700;
  (function step(now: number) {
    const k = Math.min(1, (now - t0) / dur);
    const e = 1 - Math.pow(1 - k, 3);
    el.textContent = fmt(Math.round(from + (to - from) * e));
    if (k < 1) requestAnimationFrame(step);
  })(performance.now());
}

function showWinner(): void {
  if (!live.winner || !live.prize) return;   // a live pode ter reiniciado enquanto esperávamos
  const l = leadById(live.winner.leadId);
  jackpot(); coinBlast(); Snd.coins();
  rollMoney(q('#win-money'), live.prize.value || 0);
  q('#win-name').textContent  = l ? l.name : '—';
  q('#win-tg').textContent    = l && l.telegram ? l.telegram : '';
  q('#win-num').innerHTML     = live.winner.number.split('').map(ch => `<b>${ch}</b>`).join('');
  q('#win-emoji').textContent = live.prize.emoji;
  q('#win-prize').textContent = live.prize.name;
  q('#win-said').textContent  = t('stage.spoken');
  q('#stage-seal').innerHTML  = `🔑 <b>${live.seed}</b>`;
  act('winner');
  Snd.fanfare();
  confettiBurst();
  live.revealing = false;
  updateConsole();
}

/**
 * Confirma: grava o resultado no servidor e só então marca localmente. Se o servidor recusar
 * (prêmio já sorteado, sorteio encerrado, cartela fora do bolão), nada é marcado e o palco volta
 * ao início com os dados atualizados — assim um sorteio nunca acontece duas vezes.
 */
async function confirmWinner(): Promise<void> {
  if (!live.winner || !live.prize || live.busy) return;
  const ev = live.event;
  const { winner, prize, seed, hash, order } = live;
  const poolSize = live.pool.length;
  live.busy = true; updateConsole();

  let closedByServer = false;
  if (ev) {
    try {
      const saved = await eventsApi.recordDraw(ev.id, {
        prizeId: prize.id, cartelaId: winner.id, number: winner.number,
        seed, commitHash: hash, poolSize, revealOrder: order,
      });
      closedByServer = Boolean(saved.cycle);   // último prêmio: o servidor já encerrou o ciclo
    } catch (err) {
      live.busy = false;
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        // o servidor sabe algo que este navegador não sabia: mostra o motivo e recarrega a verdade
        toast(err.message);
        try { await hydrateFromApi(); } catch { /* segue com o cache */ }
        resetLive();
        return;
      }
      // sem rede: fica local e a sincronização envia depois (o servidor ainda valida na hora)
      toast(t('msg.drawSaveFailed'));
    }
  }

  const s = getState();
  if (!s.draws.some(d => d.prizeId === prize.id)) {
    s.draws.push({
      id: uid(), at: Date.now(), eventId: ev ? ev.id : null, prizeId: prize.id,
      cartelaId: winner.id, leadId: winner.leadId, number: winner.number,
      seed, hash, poolSize,
    });
  }
  prize.status = 'drawn';
  if (s.config.burnCartela) {
    const c = s.cartelas.find(x => x.id === winner.id);
    if (c) c.status = 'won';
  }
  commit();
  live.busy = false;
  resetLive();
  // último prêmio da live: o servidor encerra o ciclo sozinho ao gravar; aqui só refletimos (ou fechamos, se ele não fechou)
  if (ev && pendingPrizes(ev.id).length === 0) {
    if (closedByServer) { toast(t('msg.eventClosed')); void hydrateFromApi().then(() => resetLive()).catch(() => { /* cache */ }); }
    else void closeCycle(ev.id);
  }
}

async function closeCycle(eventId: string): Promise<void> {
  const won = getState().draws.filter(d => d.eventId === eventId).map(d => d.cartelaId);
  try {
    const r = await eventsApi.close(eventId, won);
    toast(t(r.status === 'DONE' ? 'msg.eventClosed' : 'msg.saved'));
    await hydrateFromApi();
  } catch (err) {
    // 409 = já estava encerrado (o servidor fechou ao gravar o último prêmio): não é erro
    if (err instanceof ApiError && err.status === 409) { toast(t('msg.eventClosed')); await hydrateFromApi().catch(() => { /* cache */ }); return; }
    toast(t('msg.closeFailed'));
  }
}

/** Botão principal: uma tecla só para a live inteira. */
export function liveNext(): void {
  Snd.resume();
  if (live.phase === 'idle') {
    const ev = live.event || currentEvent();
    const next = ev ? pendingPrizes(ev.id)[0] : undefined;
    if (!next) { toast(t('msg.noprizes')); return; }
    void startDraw(next);
  } else if (live.phase === 'draw') {
    revealNext();
  } else if (live.phase === 'winner') {
    if (live.revealing) return;          // o ganhador ainda não está na tela
    void confirmWinner();
  }
}

/** Pula o prêmio da vez para o fim da fila (ou cancela o sorteio em curso). */
function skipPrize(): void {
  if (live.phase === 'draw' || live.phase === 'winner') { resetLive(); return; }
  const s = getState();
  const pend = s.prizes.filter(p => p.status === 'pending');
  if (pend.length > 1) {
    const first = pend[0] as Prize;
    s.prizes.push(s.prizes.splice(s.prizes.indexOf(first), 1)[0] as Prize);
    commit();
  }
  resetLive();
}

/** Atalhos de teclado do operador. */
export function handleKey(e: KeyboardEvent): void {
  if (!isOpen()) return;
  if (e.code === 'Space') { e.preventDefault(); liveNext(); }
  if (e.code === 'Escape') closeStage();
  if (e.key === 'w' || e.key === 'W') spinWheel();
  if (e.key === 'h' || e.key === 'H') toggleConsole();
}
